import type { Recurly } from "./recurly-js";

const SCRIPT_URL = "https://js.recurly.com/v4/recurly.js";
const CSS_URL = "https://js.recurly.com/v4/recurly.css";

let loading: Promise<Recurly> | undefined;
let configuredKey: string | undefined;

/** Rejects with `code: "SERVICE_UNAVAILABLE"`, the same code the API uses, so callers share the copy. */
const unavailable = () => Object.assign(new Error("Recurly.js failed to load"), { code: "SERVICE_UNAVAILABLE" });

const inject = () => new Promise<Recurly>((resolve, reject) => {
	if (window.recurly) {
		resolve(window.recurly);
		return;
	}
	const link = document.createElement("link");
	link.rel = "stylesheet";
	link.href = CSS_URL;
	document.head.appendChild(link);
	const script = document.createElement("script");
	script.src = SCRIPT_URL;
	script.async = true;
	script.addEventListener("load", () => (window.recurly ? resolve(window.recurly) : reject(unavailable())));
	script.addEventListener("error", () => {
		script.remove();
		link.remove();
		reject(unavailable());
	});
	document.head.appendChild(script);
});

/**
 * Loads Recurly.js on first use rather than on every page, so card fields only pull the script in
 * when a card is actually being edited. A failed load is forgotten, so the next attempt retries.
 */
export const loadRecurly = (publicKey: string): Promise<Recurly> => {
	if (!loading) {
		loading = inject().catch((error) => {
			loading = undefined;
			throw error;
		});
	}
	return loading.then((recurly) => {
		if (configuredKey !== publicKey) {
			recurly.configure({ publicKey });
			configuredKey = publicKey;
		}
		return recurly;
	});
};
